import React, { useState, FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { authApiClient, ApiError } from "@/api/authApiClient";
import { AlertsWrapper } from "@/components/AlertsWrapper";
import { useTranslation } from "react-i18next";

interface LocationState {
  email?: string;
}

export const ForgotPassword = () => {
  const location = useLocation();
  const emailFromLogin = (location.state as LocationState)?.email || "";
  const [email, setEmail] = useState<string>(emailFromLogin);
  const [successMessages, setSuccessMessages] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<ApiError[]>([]);
  const { t } = useTranslation();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setIsSubmitting(true);
    const { errors, data, success } = await authApiClient.resetPassword(email);
    if (success && data && data.message) {
      setSuccessMessages([data.message]);
    }
    setErrors(errors);
    setIsSubmitting(false);
  };

  return (
    <main role="main">
      <h2 className="mb-3 text-2xl font-medium text-foreground">
        {t("login.forgot")}
      </h2>
      <p className="mb-6 text-sm text-muted-foreground">
        {t("login.instructions")}
      </p>
      <AlertsWrapper errors={errors} messages={successMessages}>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-foreground">
              {t("login.emailLabel")}
            </label>
            <input
              disabled={isSubmitting}
              value={email}
              type="email"
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("login.email")}
              data-testid="email-input"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium text-muted-foreground focus:ring-2 focus:ring-ring focus:outline-none"
            />
          </div>
          <button
            type="submit"
            disabled={email.length < 1 || isSubmitting}
            className="flex w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed"
          >
            {isSubmitting ? t("login.loading") : t("login.send")}
          </button>
        </form>
      </AlertsWrapper>
      <div className="mt-4">
        <Link
          to="/login"
          className="inline-flex items-center text-sm font-medium text-foreground underline hover:no-underline"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t("login.back")}
        </Link>
      </div>
    </main>
  );
};
